import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, Loader2, ArrowLeft, FileText, Package, Truck, Info, Edit } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuotationEdit = () => {
  const { quotationNumber } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [originalQuotation, setOriginalQuotation] = useState(null);
  
  // Master Data
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  
  // Customer-specific data
  const [customerSalesAreas, setCustomerSalesAreas] = useState([]);
  const [shippingPointsForPlant, setShippingPointsForPlant] = useState({});

  const [header, setHeader] = useState({
    document_type: 'QT',
    customer_id: '',
    sales_organization: '',
    distribution_channel: '',
    division: '',
    quotation_date: '',
    pricing_date: '',
    requested_delivery_date: '',
    valid_from: '',
    valid_to: '',
    sold_to_party: '',
    ship_to_party: '',
    payment_terms: '',
    incoterms: '',
    incoterms_location: '',
    customer_reference: '',
    header_note: ''
  });

  const [items, setItems] = useState([]);


  const loadMasterDataAndQuotation = useCallback(async () => {
    setIsLoading(true);
    try {
      const [custRes, orgRes, dcRes, divRes, matRes, plantRes, incoRes, payRes, quotationRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/divisions`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-incoterms`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-terms-of-payments`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}`)
      ]);
      
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
      setDistChannels(dcRes.data || []);
      setDivisions(divRes.data || []);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
      setIncoterms(incoRes.data || []);
      setPaymentTerms(payRes.data || []);
      
      const quotation = quotationRes.data;
      setOriginalQuotation(quotation);
      
      // Set customer sales areas
      const selectedCustomer = (custRes.data || []).find(c => c.customer_id === quotation.customer_id);
      if (selectedCustomer) {
        setCustomerSalesAreas(selectedCustomer.sales_area_data || []);
      }
      
      // Populate header
      setHeader({
        document_type: quotation.document_type || 'QT',
        customer_id: quotation.customer_id || '',
        sales_organization: quotation.sales_organization || '',
        distribution_channel: quotation.distribution_channel || '',
        division: quotation.division || '',
        quotation_date: quotation.quotation_date || '',
        pricing_date: quotation.pricing_date || '',
        requested_delivery_date: quotation.requested_delivery_date || '',
        valid_from: quotation.valid_from || '',
        valid_to: quotation.valid_to || '',
        sold_to_party: quotation.sold_to_party || '',
        ship_to_party: quotation.ship_to_party || '',
        payment_terms: quotation.payment_terms || '',
        incoterms: quotation.incoterms || '',
        incoterms_location: quotation.incoterms_location || '',
        customer_reference: quotation.customer_reference || '',
        header_note: quotation.header_note || ''
      });
      
      // Populate items
      const loadedItems = (quotation.items || []).map(item => ({
        item_number: item.item_number,
        material_id: item.material_id || '',
        item_description: item.item_description || '',
        item_category: item.item_category || 'TAN',
        quoted_quantity: item.quoted_quantity || 1,
        unit_of_measure: item.unit_of_measure || 'EA',
        net_price: item.net_price || 0,
        plant: item.plant || '',
        plant_determined_from: item.plant_determined_from || '',
        shipping_point: item.shipping_point || '',
        pricing_info: item.pricing_info || '',
        availability_check: item.availability_check || false,
        item_note: item.item_note || ''
      }));
      
      setItems(loadedItems.length > 0 ? loadedItems : [{
        item_number: 10,
        material_id: '',
        item_description: '',
        item_category: 'TAN',
        quoted_quantity: 1,
        unit_of_measure: 'EA',
        net_price: 0,
        plant: '',
        plant_determined_from: '',
        shipping_point: '',
        pricing_info: '',
        availability_check: false,
        item_note: ''
      }]);
      
      // Load shipping points for any plants in items
      const uniquePlants = [...new Set(loadedItems.map(i => i.plant).filter(Boolean))];
      for (const plantId of uniquePlants) {
        fetchShippingPointsForPlant(plantId);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load quotation');
      navigate('/presales/quotations');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line
  }, [quotationNumber]);

  useEffect(() => {
    loadMasterDataAndQuotation();
  }, [loadMasterDataAndQuotation]);

  // Fetch shipping points for a plant
  const fetchShippingPointsForPlant = async (plantId) => {
    if (!plantId || shippingPointsForPlant[plantId]) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/shipping-points-for-plant/${plantId}`);
      setShippingPointsForPlant(prev => ({
        ...prev,
        [plantId]: response.data || []
      }));
    } catch (error) {
      console.error('Error fetching shipping points:', error);
    }
  };

  // Fetch commercial terms
  const fetchCommercialTerms = async (shipToPartyId, salesOrg, distChannel) => {
    if (!shipToPartyId || !salesOrg || !distChannel) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/presales/customer-commercial-terms/${shipToPartyId}`,
        { params: { sales_org: salesOrg, dist_channel: distChannel } }
      );
      
      if (response.data) {
        setHeader(prev => ({
          ...prev,
          incoterms: response.data.incoterms || '',
          incoterms_location: response.data.incoterms_location || '',
          payment_terms: response.data.payment_terms || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching commercial terms:', error);
    }
  };

  // Determine plant for item
  const determinePlantForItem = async (index, materialId) => {
    const shipToParty = header.ship_to_party || header.customer_id;
    if (!shipToParty || !header.sales_organization || !header.distribution_channel) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/determine-plant`, {
        params: {
          material_id: materialId || undefined,
          ship_to_party: shipToParty,
          sales_org: header.sales_organization,
          dist_channel: header.distribution_channel
        }
      });
      
      if (response.data?.plant_id) {
        const newItems = [...items];
        newItems[index].plant = response.data.plant_id;
        newItems[index].plant_determined_from = response.data.source;
        newItems[index].shipping_point = '';
        setItems(newItems);
        fetchShippingPointsForPlant(response.data.plant_id);
      }
    } catch (error) {
      console.error('Error determining plant:', error);
    }
  };

  // Get unique sales orgs from customer's sales areas
  const getCustomerSalesOrgs = () => {
    if (customerSalesAreas.length === 0) return [];
    const uniqueOrgs = [...new Set(customerSalesAreas.map(sa => sa.sales_org_id))];
    return salesOrgs.filter(org => uniqueOrgs.includes(org.sales_org_id));
  };

  // Get distribution channels for selected sales org
  const getCustomerDistChannels = () => {
    if (customerSalesAreas.length === 0 || !header.sales_organization) return [];
    const relevantAreas = customerSalesAreas.filter(sa => sa.sales_org_id === header.sales_organization);
    const uniqueChannels = [...new Set(relevantAreas.map(sa => sa.dist_channel_id))];
    return distChannels.filter(dc => uniqueChannels.includes(dc.dist_channel_id));
  };

  // Get divisions for selected sales org and distribution channel
  const getCustomerDivisions = () => {
    if (customerSalesAreas.length === 0 || !header.sales_organization || !header.distribution_channel) return [];
    const relevantAreas = customerSalesAreas.filter(
      sa => sa.sales_org_id === header.sales_organization && sa.dist_channel_id === header.distribution_channel
    );
    const uniqueDivisions = [...new Set(relevantAreas.map(sa => sa.division_id))];
    return divisions.filter(d => uniqueDivisions.includes(d.division_id));
  };

  // Get ship-to party options
  const getShipToPartyOptions = () => {
    return customers;
  };

  // Handle ship-to party change
  const handleShipToPartyChange = (shipToPartyId) => {
    setHeader({
      ...header,
      ship_to_party: shipToPartyId
    });
    
    if (shipToPartyId && header.sales_organization && header.distribution_channel) {
      fetchCommercialTerms(shipToPartyId, header.sales_organization, header.distribution_channel);
    }
    
    items.forEach((item, index) => {
      if (item.material_id) {
        determinePlantForItem(index, item.material_id);
      }
    });
  };

  // Get materials available for the selected Sales Area
  const getAvailableMaterials = () => {
    if (!header.sales_organization || !header.distribution_channel) return [];
    
    return materials.filter(m => {
      const salesData = m.sales_data || [];
      return salesData.some(
        sd => sd.sales_org_id === header.sales_organization && 
              sd.dist_channel_id === header.distribution_channel
      );
    });
  };

  const handleAddItem = () => {
    const newItemNumber = items.length > 0 ? Math.max(...items.map(i => i.item_number)) + 10 : 10;
    setItems([...items, {
      item_number: newItemNumber,
      material_id: '',
      item_description: '',
      item_category: 'TAN',
      quoted_quantity: 1,
      unit_of_measure: 'EA',
      net_price: 0,
      plant: '',
      plant_determined_from: '',
      shipping_point: '',
      pricing_info: '',
      availability_check: false,
      item_note: ''
    }]);
    setActiveItemIndex(items.length);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    if (activeItemIndex >= newItems.length) {
      setActiveItemIndex(newItems.length - 1);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'material_id' && value) {
      const material = materials.find(m => m.material_number === value);
      if (material) {
        newItems[index].item_description = material.description || '';
        newItems[index].unit_of_measure = material.base_uom || 'EA';
      }
      setItems(newItems);
      determinePlantForItem(index, value);
    } else if (field === 'plant' && value) {
      newItems[index].plant_determined_from = 'MANUAL';
      newItems[index].shipping_point = '';
      setItems(newItems);
      fetchShippingPointsForPlant(value);
    } else {
      setItems(newItems);
    }
  };

  const validateForm = () => {
    if (!header.customer_id) {
      toast.error('Customer is required');
      return false;
    }
    if (!header.sales_organization) {
      toast.error('Sales Organization is required');
      return false;
    }
    if (!header.distribution_channel) {
      toast.error('Distribution Channel is required');
      return false;
    }
    if (!header.division) {
      toast.error('Division is required');
      return false;
    }
    
    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (header.valid_from) {
      const validFromDate = new Date(header.valid_from);
      validFromDate.setHours(0, 0, 0, 0);
      if (validFromDate < today) {
        toast.error('Valid From date cannot be in the past');
        return false;
      }
    }
    
    if (header.valid_to) {
      const validToDate = new Date(header.valid_to);
      validToDate.setHours(0, 0, 0, 0);
      if (validToDate < today) {
        toast.error('Valid To date cannot be in the past');
        return false;
      }
    }
    
    if (header.valid_from && header.valid_to) {
      const validFromDate = new Date(header.valid_from);
      const validToDate = new Date(header.valid_to);
      if (validToDate <= validFromDate) {
        toast.error('Valid To date must be greater than Valid From date');
        return false;
      }
    }
    
    for (const item of items) {
      if (!item.item_description) {
        toast.error(`Item ${item.item_number}: Description is required`);
        return false;
      }
      if (!item.quoted_quantity || item.quoted_quantity <= 0) {
        toast.error(`Item ${item.item_number}: Quantity must be greater than 0`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
      // Update quotation header
      const headerPayload = {
        ship_to_party: header.ship_to_party || null,
        pricing_date: header.pricing_date || null,
        requested_delivery_date: header.requested_delivery_date || null,
        valid_from: header.valid_from || null,
        valid_to: header.valid_to || null,
        incoterms: header.incoterms || null,
        incoterms_location: header.incoterms_location || null,
        payment_terms: header.payment_terms || null,
        customer_reference: header.customer_reference || null,
        header_note: header.header_note || null
      };
      
      await axios.put(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}`, headerPayload);
      
      toast.success(`Quotation '${quotationNumber}' updated successfully!`);
      navigate(`/presales/quotations/${quotationNumber}`);
    } catch (error) {
      console.error('Error updating quotation:', error);
      toast.error(error.response?.data?.detail || 'Failed to update quotation');
    } finally {
      setIsSaving(false);
    }
  };

  const currentItem = items[activeItemIndex] || items[0];
  const currentItemShippingPoints = currentItem?.plant ? (shippingPointsForPlant[currentItem.plant] || []) : [];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading quotation...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="quotation-edit">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/presales/quotations/${quotationNumber}`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Edit className="w-6 h-6" />
                Edit Quotation {quotationNumber}
              </h1>
              <p className="text-sm text-gray-600">Modify quotation details</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-quotation-btn">
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="header">Header Data</TabsTrigger>
            <TabsTrigger value="partners">Partners & Terms</TabsTrigger>
            <TabsTrigger value="items">Items ({items.length})</TabsTrigger>
          </TabsList>

          {/* Header Tab */}
          <TabsContent value="header">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer & Organization - Read Only */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Customer & Organization</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Document Type</Label>
                    <Input value="QT - Quotation" disabled className="bg-gray-50" />
                  </div>
                  <div>
                    <Label>Customer</Label>
                    <Input 
                      value={`${header.customer_id} - ${originalQuotation?.customer_name || ''}`} 
                      disabled 
                      className="bg-gray-50" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Customer cannot be changed after creation</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sales Organization</Label>
                      <Input value={header.sales_organization} disabled className="bg-gray-50" />
                    </div>
                    <div>
                      <Label>Distribution Channel</Label>
                      <Input value={header.distribution_channel} disabled className="bg-gray-50" />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Division</Label>
                    <Input value={header.division} disabled className="bg-gray-50" />
                    <p className="text-xs text-gray-500 mt-1">
                      Sales Area: {header.sales_organization} / {header.distribution_channel} / {header.division}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Dates & Validity */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Dates & Validity</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quotation Date</Label>
                      <Input
                        type="date"
                        value={header.quotation_date}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
                    </div>
                    <div>
                      <Label>Pricing Date</Label>
                      <Input
                        type="date"
                        value={header.pricing_date}
                        onChange={(e) => setHeader({...header, pricing_date: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Requested Delivery Date</Label>
                    <Input
                      type="date"
                      value={header.requested_delivery_date}
                      onChange={(e) => setHeader({...header, requested_delivery_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valid From</Label>
                      <Input
                        type="date"
                        value={header.valid_from}
                        onChange={(e) => setHeader({...header, valid_from: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-gray-500 mt-1">Cannot be in the past</p>
                    </div>
                    <div>
                      <Label>Valid To</Label>
                      <Input
                        type="date"
                        value={header.valid_to}
                        onChange={(e) => setHeader({...header, valid_to: e.target.value})}
                        min={header.valid_from || new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-gray-500 mt-1">Must be greater than Valid From</p>
                    </div>
                  </div>
                  <div>
                    <Label>Customer Reference</Label>
                    <Input
                      value={header.customer_reference}
                      onChange={(e) => setHeader({...header, customer_reference: e.target.value})}
                      placeholder="Customer's reference number"
                      maxLength={35}
                    />
                  </div>
                  <div>
                    <Label>Header Note</Label>
                    <textarea
                      value={header.header_note}
                      onChange={(e) => setHeader({...header, header_note: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      rows={3}
                      placeholder="Additional notes..."
                      maxLength={1000}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Partners & Terms Tab */}
          <TabsContent value="partners">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Partner Functions */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Partner Functions
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Sold-to Party</Label>
                    <Input 
                      value={header.sold_to_party ? `${header.sold_to_party} - ${customers.find(c => c.customer_id === header.sold_to_party)?.name || ''}` : ''} 
                      disabled 
                      className="bg-gray-50" 
                    />
                  </div>
                  <div>
                    <Label>Ship-to Party</Label>
                    <Select 
                      value={header.ship_to_party || '__DEFAULT__'} 
                      onValueChange={(v) => handleShipToPartyChange(v === '__DEFAULT__' ? header.customer_id : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Ship-to Party" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__DEFAULT__">Same as Sold-to Party</SelectItem>
                        {getShipToPartyOptions().map(c => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.customer_id} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Commercial terms will be fetched from Ship-to Party's Customer Master
                    </p>
                  </div>
                </div>
              </Card>

              {/* Commercial Terms */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Commercial Terms</h3>
                <div className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>These terms are automatically fetched from <strong>Ship-to Party's</strong> Customer Master. You can override them if needed.</span>
                  </div>
                  <div>
                    <Label>Incoterms</Label>
                    <Select 
                      value={header.incoterms || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, incoterms: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Incoterms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {incoterms.map(i => (
                          <SelectItem key={i.incoterms_id} value={i.incoterms_id}>
                            {i.incoterms_id} - {i.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Incoterms Location</Label>
                    <Input
                      value={header.incoterms_location}
                      onChange={(e) => setHeader({...header, incoterms_location: e.target.value})}
                      placeholder="e.g., Port of destination"
                      maxLength={70}
                    />
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Select 
                      value={header.payment_terms || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, payment_terms: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Payment Terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {paymentTerms.map(p => (
                          <SelectItem key={p.payment_key} value={p.payment_key}>
                            {p.payment_key} - {p.sales_text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Item List */}
              <Card className="p-4 lg:col-span-1">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Items</h3>
                  <Button size="sm" onClick={handleAddItem}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div
                      key={`item-${item.item_number || index}`}
                      className={`p-3 rounded-lg cursor-pointer flex justify-between items-center ${
                        activeItemIndex === index ? 'bg-orange-100 border-2 border-orange-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => setActiveItemIndex(index)}
                    >
                      <div>
                        <p className="font-mono font-bold text-sm">{item.item_number.toString().padStart(4, '0')}</p>
                        <p className="text-xs text-gray-600 truncate max-w-[120px]">
                          {item.item_description || item.material_id || 'New Item'}
                        </p>
                      </div>
                      {items.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Item Details */}
              <Card className="p-6 lg:col-span-3">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Item {currentItem?.item_number?.toString().padStart(4, '0')} Details
                </h3>
                
                {currentItem && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Material</Label>
                      <Select 
                        value={currentItem.material_id || '__NONE__'} 
                        onValueChange={(v) => handleItemChange(activeItemIndex, 'material_id', v === '__NONE__' ? '' : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Material" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__NONE__">-- Manual Entry --</SelectItem>
                          {getAvailableMaterials().map(m => (
                            <SelectItem key={m.material_number} value={m.material_number}>
                              {m.material_number} - {m.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Item Category</Label>
                      <Select value={currentItem.item_category} onValueChange={(v) => handleItemChange(activeItemIndex, 'item_category', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TAN">TAN - Standard Item</SelectItem>
                          <SelectItem value="TANN">TANN - Free Item</SelectItem>
                          <SelectItem value="TAD">TAD - Service Item</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Description *</Label>
                      <Input
                        value={currentItem.item_description}
                        onChange={(e) => handleItemChange(activeItemIndex, 'item_description', e.target.value)}
                        placeholder="Item description"
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <Label>Quoted Quantity *</Label>
                      <Input
                        type="text"
                        value={currentItem.quoted_quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d*$/.test(val)) {
                            handleItemChange(activeItemIndex, 'quoted_quantity', val);
                          }
                        }}
                        placeholder="Enter quantity"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter whole numbers or decimals</p>
                    </div>
                    <div>
                      <Label>Unit of Measure</Label>
                      <Select value={currentItem.unit_of_measure} onValueChange={(v) => handleItemChange(activeItemIndex, 'unit_of_measure', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EA">EA - Each</SelectItem>
                          <SelectItem value="PC">PC - Piece</SelectItem>
                          <SelectItem value="KG">KG - Kilogram</SelectItem>
                          <SelectItem value="L">L - Liter</SelectItem>
                          <SelectItem value="M">M - Meter</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Plant & Shipping Point */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Logistics
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Plant</Label>
                          <Select 
                            value={currentItem.plant || '__NONE__'} 
                            onValueChange={(v) => handleItemChange(activeItemIndex, 'plant', v === '__NONE__' ? '' : v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Plant" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__NONE__">-- None --</SelectItem>
                              {plants.map(p => (
                                <SelectItem key={p.plant_id} value={p.plant_id}>
                                  {p.plant_id} - {p.plant_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {currentItem.plant_determined_from && (
                            <p className="text-xs text-green-600 mt-1">
                              Determined from: {currentItem.plant_determined_from === 'SHIP_TO_PARTY' ? 'Ship-to Party' : 
                                currentItem.plant_determined_from === 'MATERIAL_MASTER' ? 'Material Master' : 'Manual'}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label>Shipping Point</Label>
                          <Select 
                            value={currentItem.shipping_point || '__NONE__'} 
                            onValueChange={(v) => handleItemChange(activeItemIndex, 'shipping_point', v === '__NONE__' ? '' : v)}
                            disabled={!currentItem.plant}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={!currentItem.plant ? "Select Plant first" : "Select Shipping Point"} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__NONE__">-- None --</SelectItem>
                              {currentItemShippingPoints.map(sp => (
                                <SelectItem key={sp.shipping_point_id} value={sp.shipping_point_id}>
                                  {sp.shipping_point_id} - {sp.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Pricing */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3">Pricing Override</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Manual Net Price (Override)</Label>
                          <Input
                            type="text"
                            value={currentItem.net_price}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                handleItemChange(activeItemIndex, 'net_price', val);
                              }
                            }}
                            placeholder="Leave 0 for auto-pricing"
                          />
                          <p className="text-xs text-gray-500 mt-1">Enter 0 to use automatic pricing</p>
                        </div>
                        <div>
                          <Label>Pricing Information</Label>
                          <Input
                            value={currentItem.pricing_info}
                            onChange={(e) => handleItemChange(activeItemIndex, 'pricing_info', e.target.value)}
                            placeholder="Optional pricing notes"
                            maxLength={500}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Availability Check */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="availability-check"
                          checked={currentItem.availability_check}
                          onCheckedChange={(checked) => handleItemChange(activeItemIndex, 'availability_check', checked)}
                        />
                        <Label htmlFor="availability-check" className="cursor-pointer">
                          Perform Availability Check (Optional)
                        </Label>
                      </div>
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Item Note</Label>
                      <textarea
                        value={currentItem.item_note}
                        onChange={(e) => handleItemChange(activeItemIndex, 'item_note', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                        rows={2}
                        placeholder="Additional notes..."
                        maxLength={500}
                      />
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default QuotationEdit;
